<template>
  <section class="container-full-5">
    <h2 class="secondary-font gap-4 mt-2 mb-6">Recouvrement</h2>

    <!-- Loader initial -->
    <div v-if="initialLoading" class="widget px-8 py-7">
      <div style="display: flex; justify-content: center">
        <div class="loader dark"></div>
      </div>
    </div>

    <div v-if="startupError" class="widget px-8 py-7">
      <p class="text-red">{{ startupError }}</p>
    </div>

    <template v-if="!initialLoading && !startupError">

      <!-- SECTION 1: Wallet / Décaissement -->
      <div class="s20 widget px-6 pt-8 pb-2 mb-6">
        <div class="s10 mt--4 ml--4 mb-5">
          <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
            Wallet / Décaissement
          </span>
        </div>
        <div class="s20 gap-6 px-5 pb-8">
          <div class="clear-both h-10"></div>
          <div class="flex items-center gap-6">
            <div class="s7">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-color-black"></div>
                Solde
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.solde) }} F</h1>
              <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-black" :style="{ width: soldePercentage + '%' }">
                  {{ soldePercentage }}%
                </div>
              </div>
            </div>
            <div class="s7">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-color-1000"></div>
                Montant Décaissé
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.total_montant_decaissements) }} F</h1>
              <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: decaissementPercentage + '%' }">
                  {{ decaissementPercentage }}%
                </div>
              </div>
            </div>
            <div class="s6">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-green-light"></div>
                Dernier décaissement
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(walletInfos.last_montant_decaissement) }} F</h1>
              <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 0%;">
                  0%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="clear-both h-5"></div>

      <!-- SECTION 2: Gérer les prélèvements -->
      <div class="s20 widget px-6 pt-8 pb-2 mb-6">
        <div class="s10 mt--4 ml--4 mb-5">
          <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
            Gérer les prélèvements
          </span>
        </div>
        <div class="clear-both h-5"></div>

        <div class="s20 items-center gap-6 px-0 pb-6">
          <div class="px-5 mt-3">
            <div class="s10">
              <h2 class="secondary-font gap-4 mt-2 mb-4">
                Charger le fichier de prélèvement de {{ formData.periode ? formData.periode.label : '' }}
              </h2>
              <p class="mb-2">Format de fichier supporté: PDF. Taille max: 50MB.</p>
            </div>

            <h2 class="s10 font-medium pl-8">
              Montant à recevoir <br class="mb-4" /><span class="text-4xl font-bold ml-2">{{ formatAmount(prelevementStats.montant_prelevement_expected) }} F CFA</span>
            </h2>

            <div class="clear-both h-5"></div>

            <div class="s10 form-field-split my-4">
              <div class="form-field-floating lg active half">
                <select class="form-input-select" :value="formData.periode?.value" @change="onSelectPeriode">
                  <option :value="p.value" v-for="p in periodes" :key="p.value">{{ p.label }}</option>
                </select>
                <label>Sélectionner une période <b class="color-danger">*</b></label>
              </div>
              <hr class="hr" />
              <div class="form-field-floating lg reactive half">
                <money3 class="form-input" v-model="formData.montant_prelevement" v-bind="AMOUNT_FORMAT_CONFIG"></money3>
                <label>Montant du virement</label>
                <span>F&nbsp;CFA</span>
              </div>
            </div>
            <div class="s10_mobile pl-8">
              <label for="file-prelevement" class="mt-4 custom-upload" :class="{ active: formData.file }">
                <div class="custom-upload-st2">
                  <input id="file-prelevement" class="display-none" type="file" @change="onFileUploaded($event)" accept=".pdf" />
                  <div class="s4 custom-upload-img">.PDF</div>
                  <div class="s12">
                    <span class="custom-upload-text">{{ formData.file_name ? formData.file_name : 'Charger le fichier' }}</span> <b class="color-danger">*</b><br />
                    <span class="custom-upload-text">{{ formData.file_name ? 'Doc chargé' : 'Uploader le doc' }}</span>
                  </div>
                  <div class="s4 right"><img src="/icons/icon-upload.svg" class="custom-upload-icon" /></div>
                </div>
              </label>
            </div>
            <div class="clear-both h-15"></div>

            <!-- Barre de progression -->
            <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
              <div class="widget-floating py-0 text-xl flex-right">{{ prelevementPercentage }}%</div>
              <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-yellow" :style="{ width: prelevementPercentage + '%' }">
                <div class="text-3xl font-bold position-absolute">{{ formatAmount(formData.montant_prelevement) }} F / {{ formatAmount(prelevementStats.montant_prelevement_expected) }} F</div>
              </div>
            </div>

            <div class="clear-both h-30"></div>
            <div v-if="isFormValid" style="display: flex; flex-direction: row; justify-content: end;">
              <span v-if="!!saveError" class="text-red mr-4">{{ saveError }}</span>
              <custom-button type="button" :classesList="'btn lg py-5 black'" :loading="savingPrelevement" @click="onSubmitPrelevement">
                <span>Télécharger le fichier de prélèvement</span>
              </custom-button>
            </div>
            <div class="clear-both h-15"></div>
          </div>

          <!-- Liste des prélèvements -->
          <div style="display: flex; flex-direction: row; justify-content: space-between; margin-bottom: 10px;">
            <div class="text-red">{{ validateError }}</div>
            <button @click="loadPrelevementsInfos" class="btn black normal justify-content-center mx-0 btn-filtre">
              <img src="/icons/icon-scan.svg" class="btn-icon" alt="" />
              <span>Actualiser</span>
            </button>
          </div>
          <table class="table separator skin1">
            <thead>
              <tr>
                <th></th>
                <th>Période</th>
                <th>Montant</th>
                <th>Fichier</th>
                <th>Date</th>
                <th>Etat</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loadingPrelevements">
                <tr>
                  <td colspan="7" class="text-center py-5">
                    <div style="display: flex; justify-content: center">
                      <div class="loader dark"></div>
                    </div>
                  </td>
                </tr>
              </template>
              <template v-else-if="prelevementsList.length === 0">
                <tr>
                  <td colspan="7" class="text-center py-5">
                    Aucun fichier de prélèvement trouvé.
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr class="text-grey-700 over-effect" v-for="(prelevement, index) in prelevementsList" :key="'prelevement_' + index">
                  <td class="font-black"><img src="/icons/icon-data.svg" class="size-medium" /></td>
                  <td class="font-bold text-xl">{{ prelevement.periode_prelevement_formatted }}</td>
                  <td class="font-bold text-xl">{{ formatAmount(prelevement.montant_prelevement) }} F CFA</td>
                  <td class="font-bold"><a>{{ prelevement.fichier_prelevement }}</a></td>
                  <td class="text-sm text-grey-500">{{ prelevement.created_at_formatted }}</td>
                  <td class="text-sm text-grey-500">
                    <span class="tag-state warning stroke">{{ prelevement.status_prelevement_label }}</span>
                  </td>
                  <td class="flex-right">
                    <custom-button type="button" @click="doValidatePrelevement(prelevement)" :classesList="'btn-ultra'" :loading="validatingPrelevement" v-if="prelevement.status_prelevement === 'pending'">
                      <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                      <span>Valider</span>
                    </custom-button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="clear-both h-5"></div>

      <!-- SECTION 3: Prélèvements / Décaissements par membre -->
      <div class="s20 widget px-6 pt-8 pb-2">
        <div class="s10 mt--4 ml--4 mb-5">
          <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
            Prélèvements / Décaissements
          </span>
        </div>
        <div class="clear-both h-10"></div>

        <div class="s20 items-center gap-6 px-0 pb-6">
          <!-- Sélection de période -->
          <div class="s20 flex gap-3 mb-7">
            <span class="btn" @click="onSelectDecaissementPeriode(p)" :class="{ light: p.value === decaissementPeriode?.value, light2: p.value !== decaissementPeriode?.value }" v-for="(p, index) in periodes" :key="'dec_periode_' + index">{{ p.label }}</span>
          </div>

          <!-- Stats -->
          <div class="flex items-center px-5 gap-6">
            <div class="s7">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-pop-yellow"></div>
                Montant prélevé
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(decaissementStats.montant_prelevement) }} F</h1>
            </div>
            <div class="s7">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-color-1000"></div>
                Montant décaissé
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(decaissementStats.montant_decaissement) }} F</h1>
            </div>
            <div class="s6">
              <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                <div class="circle-medium bg-green-light"></div>
                Montant à prélever
              </h5>
              <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(decaissementStats.montant_prelevement_expected) }} F</h1>
            </div>
          </div>

          <div class="clear-both h-20"></div>

          <!-- Table des membres -->
          <table class="table separator skin3 sm rounded">
            <thead>
              <tr>
                <th></th>
                <th>Information</th>
                <th>Collectivité</th>
                <th>Prélèvement</th>
                <th>Décaissement</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loadingDecaissements">
                <tr>
                  <td colspan="5" class="text-center py-5">
                    <div style="display: flex; justify-content: center">
                      <div class="loader dark"></div>
                    </div>
                  </td>
                </tr>
              </template>
              <template v-else-if="decaissementUsers.length === 0">
                <tr>
                  <td colspan="5" class="text-center py-5">Aucun membre trouvé.</td>
                </tr>
              </template>
              <template v-else>
                <tr class="text-grey-700 over-effect" v-for="item in decaissementUsersPaginated" :key="item.id">
                  <td class="flex align-items-center">
                    <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                      <img :src="item.photo ? item.photo : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                    </div>
                  </td>
                  <td style="width: 20%;">
                    <p class="text-xl font-semibold mb-2">
                      {{ item.nom + ' ' + item.prenom }}
                    </p>
                  </td>
                  <td style="width: 15%;" class="text-xl font-semibold mb-2">
                    {{ item.collectivite_nom }}
                  </td>
                  <td>
                    <!-- Prélèvement -->
                    <div class="widget s20 border border-3 active shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="item.prelevement_id">
                      <div class="s10">
                        <h6 class="secondary-font font-semibold mb-1">{{ item.prelevement_periode_prelevement_formatted }}</h6>
                        <h3 class="secondary-font font-semibold text-grey-900 py-1">{{ formatAmount(item.prelevement_montant_prelevement) }} F</h3>
                      </div>
                      <div class="s10 text-right">
                        <div class="mb-4">
                          <span class="tag-state notif stroke">{{ item.status_prelevement_label }}</span>
                        </div>
                        <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4">Le {{ item.prelevement_created_at_formatted }}</h6>
                      </div>
                    </div>
                    <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-else>
                      <div class="s20 text-center">Aucun prélèvement</div>
                    </div>
                  </td>
                  <td>
                    <!-- Décaissement -->
                    <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="item.souscription_id" :class="{ active: item.souscription_statut === 'done' }">
                      <div class="s10">
                        <h6 class="secondary-font font-semibold mb-1">{{ item.souscription_date_prevision_formatted }}</h6>
                        <h3 class="secondary-font font-semibold text-grey-900 py-1">{{ formatAmount(item.souscription_pouvoir_achat) }} F</h3>
                      </div>
                      <div class="s10 text-right">
                        <div class="mb-4">
                          <span class="tag-state danger stroke" v-if="item.souscription_statut === 'pending'">En attente</span>
                          <span class="tag-state success stroke" v-if="item.souscription_statut === 'done'">Effectué</span>
                        </div>
                        <h6 class="secondary-font font-semibold text-grey-600 py-1">{{ item.souscription_created_at_formatted }}</h6>
                      </div>
                    </div>
                    <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-else>
                      <div class="s20 text-center">Aucun décaissement</div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <!-- Pagination -->
          <div style="display: flex; flex-direction: row; justify-content: center;">
            <custom-pagination v-model="decaissementPage" :total="decaissementUsers.length" :per-page="10" :last_page="Math.ceil(decaissementUsers.length / 10)" @update:modelValue="onDecaissementPaginate" />
          </div>
        </div>
      </div>

    </template>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { formatAmount, AMOUNT_FORMAT_CONFIG } from '@/utils/formatters'
import prelevementService from '@/services/prelevementService'
import type { Periode, PrelevementItem, DecaissementUser } from '@/services/prelevementService'

// State
const initialLoading = ref(true)
const startupError = ref('')

// Wallet
const walletInfos = reactive({
  total_montant_decaissements: 0,
  last_montant_decaissement: 0,
  total_montant_prelevements: 0,
  solde: 0
})

// Prélèvements
const periodes = ref<Periode[]>([])
const prelevementsList = ref<PrelevementItem[]>([])
const loadingPrelevements = ref(false)
const savingPrelevement = ref(false)
const validatingPrelevement = ref(false)
const saveError = ref('')
const validateError = ref('')
const prelevementStats = reactive({
  montant_prelevement_expected: 0
})

const formData = reactive({
  periode: null as Periode | null,
  montant_prelevement: 0,
  file: null as File | null,
  file_name: ''
})

// Décaissements
const decaissementPeriode = ref<Periode | null>(null)
const decaissementUsers = ref<DecaissementUser[]>([])
const decaissementPage = ref(1)
const loadingDecaissements = ref(false)
const decaissementStats = reactive({
  montant_decaissement: 0,
  montant_prelevement: 0,
  montant_prelevement_expected: 0,
  solde: 0
})

// Computed
const isFormValid = computed(() => {
  return formData.montant_prelevement > 0 && !!formData.file_name
})

const prelevementPercentage = computed(() => {
  if (!formData.montant_prelevement || !prelevementStats.montant_prelevement_expected) return 0
  return Math.min(100, Math.round((formData.montant_prelevement / prelevementStats.montant_prelevement_expected) * 100))
})

const soldePercentage = computed(() => {
  if (!walletInfos.total_montant_prelevements) return 0
  return Math.min(100, Math.round((walletInfos.solde / walletInfos.total_montant_prelevements) * 100))
})

const decaissementPercentage = computed(() => {
  if (!walletInfos.total_montant_prelevements) return 0
  return Math.min(100, Math.round((walletInfos.total_montant_decaissements / walletInfos.total_montant_prelevements) * 100))
})

const decaissementUsersPaginated = computed(() => {
  const start = (decaissementPage.value - 1) * 10
  return decaissementUsers.value.slice(start, start + 10)
})

// Init
onMounted(async () => {
  try {
    const data = await prelevementService.getStartupInfos()
    periodes.value = data.periodes
    formData.periode = data.periodes.find((p: Periode) => p.selected) || data.periodes[0] || null
    decaissementPeriode.value = formData.periode

    await Promise.all([
      loadWalletInfos(),
      loadPrelevementsInfos(),
      loadDecaissementInfos()
    ])
  } catch (error: any) {
    startupError.value = error.message || 'Erreur lors du chargement'
  } finally {
    initialLoading.value = false
  }
})

async function loadWalletInfos() {
  try {
    const data = await prelevementService.getWalletInfos()
    Object.assign(walletInfos, data)
  } catch (error: any) {
    console.error('Erreur wallet:', error)
  }
}

async function loadPrelevementsInfos() {
  if (!formData.periode) return
  try {
    loadingPrelevements.value = true
    const data = await prelevementService.getPrelevementsInfos(formData.periode.value)
    prelevementsList.value = data.prelevements
    prelevementStats.montant_prelevement_expected = data.montant_prelevement_expected
  } catch (error: any) {
    console.error('Erreur prélèvements:', error)
  } finally {
    loadingPrelevements.value = false
  }
}

async function loadDecaissementInfos() {
  if (!decaissementPeriode.value) return
  try {
    loadingDecaissements.value = true
    const data = await prelevementService.getDecaissementInfos(decaissementPeriode.value.value)
    decaissementUsers.value = data.users
    decaissementStats.montant_decaissement = data.montant_decaissement
    decaissementStats.montant_prelevement = data.montant_prelevement
    decaissementStats.montant_prelevement_expected = data.montant_prelevement_expected
    decaissementStats.solde = data.solde
    decaissementPage.value = 1
  } catch (error: any) {
    console.error('Erreur décaissements:', error)
  } finally {
    loadingDecaissements.value = false
  }
}

function onSelectPeriode(event: Event) {
  const target = event.target as HTMLSelectElement
  const selected = periodes.value.find(p => p.value === target.value)
  if (selected) {
    formData.periode = selected
    loadPrelevementsInfos()
  }
}

function onSelectDecaissementPeriode(periode: Periode) {
  decaissementPeriode.value = periode
  loadDecaissementInfos()
}

function onFileUploaded(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    formData.file = target.files[0]
    formData.file_name = target.files[0].name
    target.value = ''
  }
}

async function onSubmitPrelevement() {
  if (!formData.periode || formData.montant_prelevement <= 0) return
  savingPrelevement.value = true
  saveError.value = ''
  try {
    await prelevementService.savePrelevement({
      periode_prelevement: formData.periode.value,
      montant_prelevement: formData.montant_prelevement,
      fichier_prelevement: formData.file
    })
    // Reset form
    formData.montant_prelevement = 0
    formData.file = null
    formData.file_name = ''
    // Reload data
    await Promise.all([loadPrelevementsInfos(), loadWalletInfos()])
  } catch (error: any) {
    saveError.value = error.message
  } finally {
    savingPrelevement.value = false
  }
}

async function doValidatePrelevement(prelevement: PrelevementItem) {
  if (!window.confirm('Voulez-vous vraiment valider ce prélèvement ?')) return
  validatingPrelevement.value = true
  validateError.value = ''
  try {
    await prelevementService.validatePrelevement(prelevement.id)
    await Promise.all([loadPrelevementsInfos(), loadWalletInfos()])
  } catch (error: any) {
    validateError.value = error.message
  } finally {
    validatingPrelevement.value = false
  }
}

function onDecaissementPaginate(page: number) {
  decaissementPage.value = page
}
</script>

<style scoped>
</style>
